import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import ModalContext from './ModalContext';
import { useModalManager } from './useModalManager';
import ModalRenderer from './ModalRenderer';

/**
 * ModalProvider wraps children with the modal context and renders the modal stack.
 *
 * Usage:
 * ```jsx
 * <ModalProvider
 *   closeIconSrc="/icons/close.svg"
 *   translateTitle={(key) => t(key)}
 *   onModalOpen={(name) => track('modal_open', name)}
 *   onModalClose={(name) => track('modal_close', name)}
 * >
 *   <App />
 * </ModalProvider>
 * ```
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.closeIconSrc] - Icon URL for modal close buttons
 * @param {Function} [props.translateTitle] - (key: string) => string — translates modal title keys
 * @param {Function} [props.onModalOpen] - (name: string) => void — called when a modal opens
 * @param {Function} [props.onModalClose] - (name: string) => void — called when a modal closes
 * @param {boolean} [props.forcePortal=false] - When true, modals always render via the portal
 *   (sibling of `.wrapper`) — even on desktop. Use this when the Provider is mounted
 *   ABOVE the `.wrapper` (i.e. the React tree above DynamicWrapper). Without this, the
 *   desktop modal renders inline as a sibling of the wrapper and inherits the parent's
 *   width (which is the document root → full viewport).
 */
const ModalProvider = ({ children, closeIconSrc, translateTitle, onModalOpen, onModalClose, forcePortal = false }) => {
  const manager = useModalManager();

  // Wrap openModal/closeModal to add tracking hooks
  const trackedOpenModal = useCallback((modalDef, data, onCloseCallback) => {
    if (onModalOpen) onModalOpen(modalDef.name);
    return manager.openModal(modalDef, data, onCloseCallback);
  }, [manager.openModal, onModalOpen]);

  const trackedCloseModal = useCallback(async (modalIndex, callbackData) => {
    // Find the modal being closed for tracking
    const idx = modalIndex === undefined ? manager.modalStack.length - 1 : modalIndex;
    const modal = manager.modalStack[idx];
    const result = await manager.closeModal(modalIndex, callbackData);
    if (modal && onModalClose) onModalClose(modal.name);
    return result;
  }, [manager.modalStack, manager.closeModal, onModalClose]);

  const contextValue = useMemo(() => ({
    ...manager,
    // Alias for backwards compatibility
    modalList: manager.modalStack,
    openModal: trackedOpenModal,
    closeModal: trackedCloseModal,
    setModalList: () => {
      // no-op — maintained for API compat, use openModal/closeModal
    },
  }), [manager, trackedOpenModal, trackedCloseModal]);

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      <ModalRenderer
        closeIconSrc={closeIconSrc}
        translateTitle={translateTitle}
        forcePortal={forcePortal}
      />
    </ModalContext.Provider>
  );
};

ModalProvider.propTypes = {
  children: PropTypes.node.isRequired,
  closeIconSrc: PropTypes.string,
  translateTitle: PropTypes.func,
  onModalOpen: PropTypes.func,
  onModalClose: PropTypes.func,
  forcePortal: PropTypes.bool,
};

export default ModalProvider;
